import type { ChatMessageDto } from '@aiai/shared';

/**
 * Pure message-grouping logic for the PM chat thread, kept out of
 * ChatThread.tsx so the component file only exports components
 * (react-refresh/only-export-components) and the grouping rules stay
 * unit-testable without rendering.
 */

/** Same-sender messages closer together than this collapse under one header. */
export const GROUP_WINDOW_MS = 5 * 60 * 1000;

export function sameDay(a: string, b: string): boolean {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}

function senderKey(m: ChatMessageDto): string {
  return m.role === 'pm' ? 'pm' : `user:${m.authorId ?? m.authorEmail ?? '?'}`;
}

export interface AnnotatedMessage {
  message: ChatMessageDto;
  /** Render a date divider chip before this message. */
  showDayDivider: boolean;
  /** Start of a new group — render the avatar + name + time header. */
  showHeader: boolean;
}

/**
 * Slack-style grouping: consecutive messages from the same sender within a
 * 5-minute window (and the same calendar day) collapse under one header; the
 * first message of each new day gets a divider.
 */
export function annotateMessages(
  messages: readonly ChatMessageDto[],
): AnnotatedMessage[] {
  return messages.map((message, i) => {
    const prev = i > 0 ? messages[i - 1] : null;
    const dayBreak = !prev || !sameDay(prev.createdAt, message.createdAt);
    const senderBreak = !prev || senderKey(prev) !== senderKey(message);
    const gap =
      !prev ||
      new Date(message.createdAt).getTime() -
        new Date(prev.createdAt).getTime() >=
        GROUP_WINDOW_MS;
    return {
      message,
      showDayDivider: dayBreak,
      showHeader: dayBreak || senderBreak || gap,
    };
  });
}
